import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { clearStationState } from "./clear-station-state";
import { requestCajaCerradaNotice } from "./caja-cerrada-notice-bridge";
import { markSkipCashierAutoResume } from "./caja-cerrada-session";
import { queryClient } from "./query-client";

/**
 * Espejo de `App\Http\Util\CajaCerradaResponse` (PHP).
 * El backend responde siempre HTTP 400 con este cuerpo JSON.
 */
export const CAJA_CERRADA = {
  ERROR_CODE: "CAJA_CERRADA",
  MESSAGE:
    "La sesión de caja ha finalizado. Por favor, inicie sesión nuevamente.",
  HTTP_STATUS: 400,
} as const;

/** @deprecated Usar `CAJA_CERRADA.ERROR_CODE` */
export const CAJA_CERRADA_ERROR_CODE = CAJA_CERRADA.ERROR_CODE;

export type CajaCerradaApiBody = {
  success: false;
  error_code: typeof CAJA_CERRADA.ERROR_CODE;
  message: string;
};

/** @deprecated Usar `CajaCerradaApiBody` */
export type CajaCerradaResponse = CajaCerradaApiBody;

/** Equivalente a `CajaCerradaResponse::toArray()` en PHP. */
export function cajaCerradaToArray(): CajaCerradaApiBody {
  return {
    success: false,
    error_code: CAJA_CERRADA.ERROR_CODE,
    message: CAJA_CERRADA.MESSAGE,
  };
}

let handlingCajaCerrada = false;
let suppressCajaCerradaHandling = false;

/** Evita redirección a /stores durante cierre de caja intencional. */
export function setCajaCerradaHandlingSuppressed(suppressed: boolean): void {
  suppressCajaCerradaHandling = suppressed;
}

export function suppressCajaCerradaHandlingActive(): boolean {
  return suppressCajaCerradaHandling;
}

function shouldHandleCajaCerrada(): boolean {
  return !suppressCajaCerradaHandling && !handlingCajaCerrada;
}

function parseRequestBody(data: unknown): Record<string, unknown> | undefined {
  if (typeof data === "object" && data !== null) {
    return data as Record<string, unknown>;
  }

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data) as unknown;
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return undefined;
    }
  }

  return undefined;
}

/** POST de reenvío de venta creada offline (no debe redirigir a /stores). */
export function hasOfflineUuidInPayload(data: unknown): boolean {
  const record = parseRequestBody(data);
  if (!record) return false;

  const uuid = record.offline_uuid;
  return typeof uuid === "string" && uuid.length > 0;
}

export function isOfflineSaleSyncRequest(
  config?: AxiosError["config"]
): boolean {
  if (!config) return false;

  const method = (config.method ?? "get").toLowerCase();
  if (method !== "post") return false;

  const url = `${config.baseURL ?? ""}${config.url ?? ""}`;
  if (!url.includes("sales")) return false;

  return hasOfflineUuidInPayload(config.data);
}

function shouldRedirectOnCajaCerrada(
  config?: AxiosError["config"],
  requestBody?: unknown
): boolean {
  if (!shouldHandleCajaCerrada()) return false;
  if (isOfflineSaleSyncRequest(config)) return false;
  if (hasOfflineUuidInPayload(requestBody)) return false;
  return true;
}

function parseResponsePayload(data: unknown): CajaCerradaApiBody | undefined {
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data) as unknown;
      return isCajaCerradaResponse(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  if (isCajaCerradaResponse(data)) {
    return data;
  }

  return undefined;
}

/** Equivalente a `CajaCerradaResponse::is()` en PHP. */
export function isCajaCerradaResponse(
  data: unknown
): data is CajaCerradaApiBody {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const record = data as Record<string, unknown>;
  return record.error_code === CAJA_CERRADA.ERROR_CODE;
}

/** @deprecated Usar `isCajaCerradaResponse` */
export const isCajaCerradaPayload = isCajaCerradaResponse;

export function isCajaCerradaError(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.data === undefined) {
    return false;
  }

  return isCajaCerradaResponse(error.response.data);
}

export function getCajaCerradaMessage(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.data !== undefined) {
    const message = parseResponsePayload(error.response.data)?.message?.trim();
    if (message) {
      return message;
    }
  }

  if (isCajaCerradaResponse(error)) {
    const message = error.message?.trim();
    if (message) {
      return message;
    }
  }

  return CAJA_CERRADA.MESSAGE;
}

function resolveCajaCerradaMessage(messageOrError?: string | unknown): string {
  if (typeof messageOrError === "string") {
    return messageOrError;
  }

  if (messageOrError) {
    return getCajaCerradaMessage(messageOrError);
  }

  return CAJA_CERRADA.MESSAGE;
}

export function createCajaCerradaAxiosError(
  data: CajaCerradaApiBody,
  config?: AxiosError["config"]
): AxiosError<CajaCerradaApiBody> {
  const resolvedConfig = (config ?? {}) as InternalAxiosRequestConfig;

  return new AxiosError(
    data.message ?? CAJA_CERRADA.MESSAGE,
    AxiosError.ERR_BAD_REQUEST,
    resolvedConfig,
    undefined,
    {
      status: CAJA_CERRADA.HTTP_STATUS,
      statusText: "Bad Request",
      headers: {},
      config: resolvedConfig,
      data,
    }
  );
}

/**
 * Si el body es CAJA_CERRADA (p. ej. sync offline con fetch), redirige y devuelve true.
 */
export function tryHandleCajaCerradaFromHttp(
  status: number,
  bodyText: string,
  options?: { requestBody?: unknown }
): boolean {
  if (status !== CAJA_CERRADA.HTTP_STATUS) {
    return false;
  }

  try {
    const json = JSON.parse(bodyText) as unknown;
    if (!isCajaCerradaResponse(json)) {
      return false;
    }

    if (!shouldRedirectOnCajaCerrada(undefined, options?.requestBody)) {
      return false;
    }

    void handleCajaCerradaRedirect(json);
    return true;
  } catch {
    return false;
  }
}

/**
 * Limpia el turno local y redirige a selección de caja.
 */
export async function handleCajaCerradaRedirect(
  messageOrError?: string | unknown
): Promise<void> {
  if (!shouldHandleCajaCerrada()) {
    return;
  }

  handlingCajaCerrada = true;

  const message = resolveCajaCerradaMessage(messageOrError);

  queryClient.cancelQueries();
  await requestCajaCerradaNotice(message);
  markSkipCashierAutoResume();
  await clearStationState({ preserveAuth: true });

  window.location.assign("/stores");
}

/** @deprecated Usar `handleCajaCerradaRedirect` */
export async function handleCajaCerrada(
  _navigate?: unknown,
  messageOrError?: string | unknown
): Promise<void> {
  await handleCajaCerradaRedirect(messageOrError);
}
